import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ShieldCheckIcon, ShipIcon, FlaskConicalIcon, GlobeIcon, CheckIcon } from 'lucide-react';
import { company } from '../data/company';
import { commodities } from '../data/products';
import { CommodityCard } from '../components/CommodityCard';
import { SectionHeading } from '../components/SectionHeading';
import { LiveOrderFeed } from '../components/LiveOrderFeed';
import { OperatingRegions } from '../components/OperatingRegions';
import { useLiveOrders } from '../hooks/useLiveOrders';
import { quickOrderMailto, whatsappLink } from '../utils/contact';
const pillars = [{
  icon: FlaskConicalIcon,
  title: 'Verified before it moves',
  text: 'Every lot is fire-assayed or sampled on site and re-verified by SGS or Alfred H. Knight before release to the buyer.'
}, {
  icon: ShieldCheckIcon,
  title: 'Documented chain of custody',
  text: 'ITSCI traceability, Kimberley Process certificates and full CEEC export documentation travel with the cargo.'
}, {
  icon: ShipIcon,
  title: 'Corridors we control',
  text: 'Bonded warehousing and sealed containers through Kasumbalesa to Dar es Salaam, Durban, Mombasa and Beira.'
}, {
  icon: GlobeIcon,
  title: 'Buyers in 38 countries',
  text: 'Industrial buyers, mills, manufacturers and gem houses across Asia, Europe, the Gulf and the Americas since 2010.'
}];
export function Home() {
  const {
    orders,
    latestId
  } = useLiveOrders();
  const weekTotal = orders.filter(o => Date.now() - o.timestamp.getTime() < 7 * 24 * 60 * 60 * 1000).length;
  return <>
      <section className="relative isolate overflow-hidden border-b border-hair" data-id="element-346" data-mp="239j930yzxgkx">
        <img src={company.images.hero} alt="Open-pit copper mine in Haut-Katanga at golden hour" className="absolute inset-0 h-full w-full object-cover" data-id="element-347" data-mp="1z7ftc05anptu" />
        <div className="absolute inset-0 bg-ink/85" aria-hidden="true" data-id="element-348" data-mp="1kkqmfd8pqadf" />

        <div className="relative mx-auto max-w-shell px-6 py-24 lg:py-32" data-id="element-349" data-mp="1kjfmjoie1mrl">
          <motion.div initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: [0.23, 1, 0.32, 1]
        }} className="max-w-3xl" data-id="element-350" data-mp="1on3qlx7ta083">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold" data-id="element-351" data-mp="1fnqfz17yb5ls">
              Mining &amp; Trading Company · Lubumbashi, D.R. Congo · Since {company.established}
            </p>
            <h1 className="mt-6 font-display text-5xl leading-[1.05] text-bone sm:text-6xl lg:text-7xl" data-id="element-352" data-mp="28ce5j8zqgh55">
              We mine, we trade,
              <br data-id="element-353" data-mp="1yjynkbofrsk9" />
              we export Congolese
              <br data-id="element-354" data-mp="1yinj09n62gs3" />
              <span className="text-gold-light" data-id="element-355" data-mp="2ma2scmdd1ycu">minerals worldwide.</span>
            </h1>
            <p className="mt-7 max-w-xl text-lg leading-relaxed text-bone/75" data-id="element-356" data-mp="2m89jd0f9z21o">
              {company.name} is a mining and trading house dealing in gold, silver, LME Grade A copper
              cathodes, copper concentrates, Kimberley Process rough diamonds and coloured gemstones —
              under one contract, one assay standard and one documented chain of custody.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4" data-id="element-357" data-mp="og8e0wtzoywp">
              <a href={quickOrderMailto()} className="inline-flex items-center gap-2 rounded-sm bg-gold px-7 py-4 text-sm font-semibold text-ink transition-colors duration-150 hover:bg-gold-light" data-id="element-358" data-mp="kdtvyey5a36h">
                Place an Order
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-359" data-mp="5nwe0gqt69pq" />
              </a>
              <a href={whatsappLink()} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-sm border border-[#25D366] px-7 py-4 text-sm font-semibold text-[#25D366] transition-colors duration-150 hover:bg-[#25D366] hover:text-[#0a2f16]" data-id="element-360" data-mp="o1nieay3m57">
                WhatsApp the desk
              </a>
              <Link to="/commodities" className="text-sm font-semibold text-bone/80 underline decoration-gold/50 underline-offset-8 transition-colors duration-150 hover:text-bone" data-id="element-361" data-mp="2ccg8ouhtyysv">
                View specifications
              </Link>
            </div>
          </motion.div>
        </div>

        <div className="relative border-t border-hair/70 bg-ink/70 backdrop-blur" data-id="element-362" data-mp="2qc7s5ar497e9">
          <dl className="mx-auto grid max-w-shell grid-cols-2 gap-y-8 px-6 py-8 lg:grid-cols-4" data-id="element-363" data-mp="2vaj35ot5dny2">
            {company.stats.map(stat => <div key={stat.label} data-id="element-364" data-mp="304wrx8db0wtr">
                <dt className="sr-only" data-id="element-365" data-mp="351wwfeui9rir">{stat.label}</dt>
                <dd data-id="element-366" data-mp="353hs57sk3gi7">
                  <span className="block font-display text-3xl text-gold-light" data-id="element-367" data-mp="3a104z99p2fvh">{stat.value}</span>
                  <span className="mt-1 block text-xs text-muted" data-id="element-368" data-mp="3e223fnvsenwe">{stat.label}</span>
                </dd>
              </div>)}
          </dl>
        </div>
      </section>

      <section className="mx-auto max-w-shell px-6 py-20 lg:py-24" data-id="element-369" data-mp="12cnavg7mtuf6">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between" data-id="element-370" data-mp="ybtmymu6wfma">
          <SectionHeading eyebrow="What we trade" title="Six commodity lines, one export desk" intro="Each line runs on its own assay, packaging and inspection protocol. Minimum lots, capacity and incoterms are published up front so buyers can qualify us before the first call." data-id="element-371" data-mp="tf38u51y3u3i" />
          <Link to="/commodities" className="inline-flex shrink-0 items-center gap-2 border-b border-gold pb-1 text-sm font-semibold text-gold-light transition-colors duration-150 hover:text-gold" data-id="element-372" data-mp="1h5z3d9xsd3eg">
            Full specification sheets
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-373" data-mp="24u8jpwc8cyy7" />
          </Link>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-374" data-mp="201yr28jqdks8">
          {commodities.map(commodity => <CommodityCard key={commodity.id} commodity={commodity} data-id="element-375" data-mp="3ucp15nyi39qy" />)}
        </div>
      </section>

      <section className="border-y border-hair bg-panel2/40 py-20 lg:py-24" data-id="element-376" data-mp="28mhk41387vm">
        <div className="mx-auto grid max-w-shell gap-12 px-6 lg:grid-cols-[1.05fr_1fr] lg:items-start" data-id="element-377" data-mp="3kkixvi2st5wg">
          <div data-id="element-378" data-mp="3pi1cnp3hyfcd">
            <SectionHeading eyebrow="Live from the desk" title="The order book, as it happens" intro="Confirmed purchase orders stream in from buyers across Asia, Europe, the Gulf and the Americas. Quantities, destinations and timestamps are logged the moment a contract is countersigned." data-id="element-379" data-mp="3phj6o0r18mr9" />
            <div className="mt-8 grid grid-cols-2 gap-6 border-t border-hair pt-8" data-id="element-380" data-mp="36m1flzvnwllx">
              <div data-id="element-381" data-mp="zu0jao1jzuv4">
                <p className="font-display text-4xl text-gold-light" data-id="element-382" data-mp="zvv8wfnrsb5j">{weekTotal}</p>
                <p className="mt-1 text-xs text-muted" data-id="element-383" data-mp="14twt53wxmgil">Orders confirmed in the last 7 days</p>
              </div>
              <div data-id="element-384" data-mp="1eymtx6glnel8">
                <p className="font-display text-4xl text-gold-light" data-id="element-385" data-mp="1extn65hn4p33">{orders.length}</p>
                <p className="mt-1 text-xs text-muted" data-id="element-386" data-mp="1a01w1dotl6zy">Orders in the rolling 8-week ledger</p>
              </div>
            </div>
            <Link to="/live-orders" className="mt-8 inline-flex items-center gap-2 rounded-sm border border-hair px-5 py-3 text-sm font-semibold text-bone transition-colors duration-150 hover:border-gold hover:text-gold-light" data-id="element-387" data-mp="1nrq7der8vrfv">
              Open the full order book
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-388" data-mp="26ordj2wjbxle" />
            </Link>
          </div>
          <LiveOrderFeed orders={orders} latestId={latestId} limit={6} data-id="element-389" data-mp="22n63dpbdxxew" />
        </div>
      </section>

      <section className="mx-auto max-w-shell px-6 py-20 lg:py-24" data-id="element-390" data-mp="caqcqchfd0io">
        <div className="grid gap-14 lg:grid-cols-[1fr_1.1fr] lg:items-center" data-id="element-391" data-mp="cb0526pxzjrj">
          <div className="relative" data-id="element-392" data-mp="8apqi5o33l12">
            <img src={company.images.logistics} alt="Sealed container being loaded with mineral cargo for export" className="w-full border border-hair object-cover" loading="lazy" data-id="element-393" data-mp="8bhjr9u7l6o6" />
            <img src={company.images.lab} alt="Technician assaying ore samples in the laboratory" className="absolute -bottom-10 -right-6 hidden w-52 border border-hair object-cover lg:block" loading="lazy" data-id="element-394" data-mp="16l63zolngv3z" />
          </div>
          <div data-id="element-395" data-mp="ivbspbgzbelu">
            <SectionHeading eyebrow="How we operate" title="Sourcing discipline, then logistics" intro="We buy only from licensed concessions and cooperatives, verify grade before payment, and keep the cargo under our own seal until it reaches the buyer's nominated warehouse or vessel." data-id="element-396" data-mp="iutz6mlpbehm" />
            <div className="mt-10 grid gap-8 sm:grid-cols-2" data-id="element-397" data-mp="23a5cn4q5bywp">
              {pillars.map(pillar => <div key={pillar.title} data-id="element-398" data-mp="2ddsv3nqk42v1">
                  <pillar.icon className="h-5 w-5 text-gold" aria-hidden="true" data-id="element-399" data-mp="2dcr7ykw9npwy" />
                  <h3 className="mt-4 text-base font-semibold text-bone" data-id="element-400" data-mp="28epq23aktwn2">{pillar.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted" data-id="element-401" data-mp="23nheb0lrxz6q">{pillar.text}</p>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      <OperatingRegions data-id="element-402" data-mp="1uo41pz2csuhs" />

      <section className="border-t border-hair bg-panel py-16" data-id="element-403" data-mp="1uoma1u2aiz62">
        <div className="mx-auto grid max-w-shell gap-10 px-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-center" data-id="element-404" data-mp="1porehd7klleo">
          <div data-id="element-405" data-mp="1kovahdyvhtz4">
            <h2 className="font-display text-3xl text-bone" data-id="element-406" data-mp="1kqg676wxbiyk">Compliance is the product</h2>
            <p className="mt-4 text-sm leading-relaxed text-muted" data-id="element-407" data-mp="1gol7ma6dnjoh">
              Buyers are not only purchasing metal or stones — they are purchasing paperwork that
              survives due diligence at the destination.
            </p>
          </div>
          <ul className="grid gap-4 sm:grid-cols-2" data-id="element-408" data-mp="3azq48uj1qvub">
            {company.certifications.map(item => <li key={item} className="flex gap-3 text-sm text-bone/85" data-id="element-409" data-mp="cqfv85spvho4">
                <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-410" data-mp="8mq8zmzilc69" />
                {item}
              </li>)}
          </ul>
        </div>
      </section>

      <section className="relative isolate overflow-hidden border-t border-hair" data-id="element-411" data-mp="ho7k0qy2eno3">
        <div className="mx-auto max-w-shell px-6 py-20 text-center lg:py-24" data-id="element-412" data-mp="3q4j3smdh7dmk">
          <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-gold" data-id="element-413" data-mp="3l4wrcf5h21et">
            Ready when you are
          </p>
          <h2 className="mx-auto mt-5 max-w-3xl font-display text-4xl leading-tight text-bone sm:text-5xl" data-id="element-414" data-mp="3phnpdj2ks0s">
            Send your specification. Receive a firm offer within 24 hours.
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-4" data-id="element-415" data-mp="37bfaj24672sh">
            <a href={quickOrderMailto()} className="rounded-sm bg-gold px-7 py-4 text-sm font-semibold text-ink transition-colors duration-150 hover:bg-gold-light" data-id="element-416" data-mp="32eowekbxeh9p">
              Place an Order
            </a>
            <a href={whatsappLink()} target="_blank" rel="noreferrer" className="rounded-sm border border-[#25D366] px-7 py-4 text-sm font-semibold text-[#25D366] transition-colors duration-150 hover:bg-[#25D366] hover:text-[#0a2f16]" data-id="element-417" data-mp="1moo0g005vd6l">
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </>;
}